import { ASK_AN_EXPERT, FORUM } from 'mk/autogenerated/translations/structuredData.c02ede25e8f86ab9300b15ddcf7e1e9e'
import { photoUrl } from 'mk/photo/photoUrl';
import { blogsProfileUrl } from 'mk2/apps/blogs/urls';
import { counsellingsHomeUrl, counsellingsPostDetailUrl } from 'mk2/apps/counsellings/urls';
import { topicUrl } from 'mk2/apps/forum/helpers';
import { CategoryEntity, PostEntity, TopicEntity } from 'mk2/apps/forum/schemas';
import { forumPostDetailUrl, forumTopicsCategoryUrl, forumUrl } from 'mk2/apps/forum/urls';
import { isCounsellingPost, largestAvailablePhotoVersionOfPost } from 'mk2/apps/forum/utils';
import { getAllPlaintext } from 'mk2/helpers/article_ast/utils';
import { getUrlOfFirstImageOrFallback } from 'mk2/helpers/structuredData';
import { YMDHMSIso } from 'mk2/helpers/timeformat';
import { url } from 'mk2/helpers/urls';
import { PhotoEntity } from 'mk2/schemas';
import { BreadcrumbList, CommentAction, DiscussionForumPosting, ListItem, WithContext } from 'schema-dts';

const breadcrumbCounsellings = (baseUrl: string): ListItem => ({
    '@type': 'ListItem',
    'position': 1,
    'item': {
        '@id': baseUrl + counsellingsHomeUrl,
        'name': ASK_AN_EXPERT,
    },
});

const breadcrumbForum = (baseUrl: string): ListItem => ({
    '@type': 'ListItem',
    'position': 1,
    'item': {
        '@id': baseUrl + forumUrl,
        'name': FORUM,
    },
});

const breadcrumbSubforum = (baseUrl: string, subforum: string, subforumUrl: string): ListItem => ({
    '@type': 'ListItem',
    'position': 2,
    'item': {
        '@id': baseUrl + subforumUrl,
        'name': subforum,
    },
});

const breadcrumbTopic = (baseUrl: string, topic: TopicEntity): ListItem => ({
    '@type': 'ListItem',
    'position': 2,
    'item': {
        '@id': baseUrl + topicUrl(topic),
        'name': topic.name,
    },
});

const breadcrumbCategory = (baseUrl: string, category: CategoryEntity): ListItem => ({
    '@type': 'ListItem',
    'position': 2,
    'item': {
        '@id': baseUrl + url(forumTopicsCategoryUrl, { categorySlug: category.slug }),
        'name': category.name,
    },
});

const breadcrumbPost = (baseUrl: string, topic: TopicEntity, post: PostEntity, route = forumPostDetailUrl): ListItem => ({
    '@type': 'ListItem',
    'position': 3,
    'item': {
        '@id': baseUrl + url(route, { topicSlug: topic.slug, postSlug: post.slug }),
        'name': post.heading,
    },
});

export function breadcrumbsCounsellings(baseUrl, topic?: TopicEntity, post?: PostEntity): WithContext<BreadcrumbList> {
    const items: ListItem[] = [breadcrumbCounsellings(baseUrl)];

    if (topic) {
        items.push(breadcrumbTopic(baseUrl, topic));
    }

    if (post) {
        items.push(breadcrumbPost(baseUrl, topic, post, counsellingsPostDetailUrl));
    }

    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': items,
    };
}

export function breadcrumbsForum(baseUrl, category?: CategoryEntity, topic?: TopicEntity, post?: PostEntity): WithContext<BreadcrumbList> {
    const items: ListItem[] = [breadcrumbForum(baseUrl)];

    if (category) {
        items.push(breadcrumbCategory(baseUrl, category));
    }

    if (topic) {
        items.push(breadcrumbTopic(baseUrl, topic));
    }

    if (post) {
        items.push(breadcrumbPost(baseUrl, topic, post));
    }

    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': items,
    };
}

export function breadcrumbsSubforum(baseUrl: string, subForum: string, subforumUrl: string): WithContext<BreadcrumbList> {
    const items: ListItem[] = [breadcrumbForum(baseUrl)];

    items.push(breadcrumbSubforum(baseUrl, subForum, subforumUrl));

    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': items,
    };
}

export function discussionForumPosting(baseUrl: string, post: PostEntity): WithContext<DiscussionForumPosting> {
    const createdOn: string = YMDHMSIso(post.created);
    const modifiedOn: string = YMDHMSIso(post.lastMessageTime);
    const { photo: mainPhoto, version: mainPhotoVersion } = mainPhotoOfPost(post);

    // fields according to https://developers.google.com/search/docs/appearance/structured-data/discussion-forum
    return {
        '@context': 'https://schema.org',
        '@type': 'DiscussionForumPosting',
        '@id': baseUrl + url(forumPostDetailUrl, { topicSlug: post.topic.slug, postSlug: post.slug }),
        'headline': post.heading,
        'text': post.perexAST ? getAllPlaintext(post.perexAST) : undefined,
        'image': mainPhoto ? photoUrl(mainPhoto, mainPhotoVersion) : undefined,
        'url': baseUrl + url(forumPostDetailUrl, { topicSlug: post.topic.slug, postSlug: post.slug }),
        'datePublished': createdOn,
        'dateModified': createdOn !== modifiedOn ? modifiedOn : undefined,
        'author': post.author?.username && {
            '@type': 'Person',
            'name': post.author.username,
            'url': baseUrl + url(blogsProfileUrl, { username: post.author.username }),
        },
        'interactionStatistic': {
            '@type': 'InteractionCounter',
            'interactionType': { '@type': 'CommentAction' },
            'userInteractionCount': post.comments,
        },
    };
}

function mainPhotoOfPost(post: PostEntity): { photo: PhotoEntity; version: string } {
    if (post.messagePhotos?.length > 0) {
        return {
            photo: post.messagePhotos[0].photofile,
            version: largestAvailablePhotoVersionOfPost(post.messagePhotos[0].photofile, 's1600x1600'),
        };
    } else if (post.photos.length > 0) {
        return {
            photo: post.photos[0],
            version: largestAvailablePhotoVersionOfPost(post.photos[0], 's1600x1600'),
        };
    } else if (isCounsellingPost(post) && post.topic.coverPhoto) {
        return {
            photo: post.topic.coverPhoto,
            version: 's1600x1600',
        };
    } else {
        return {
            photo: null,
            version: null,
        };
    }
}

export function topicOG(baseUrl: string, topic: TopicEntity) {
    return {
        'og:image': getUrlOfFirstImageOrFallback(topic.coverPhoto),
    };
}

export function postOG(baseUrl: string, post: PostEntity) {
    const { photo: mainPhoto, version: mainPhotoVersion } = mainPhotoOfPost(post);

    return {
        'og:image': mainPhoto ? photoUrl(mainPhoto, mainPhotoVersion) : getUrlOfFirstImageOrFallback(mainPhoto),
    };
}
